import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  ThumbsUp,
  MessageSquare,
  GripVertical,
  X,
  Check,
  Trash2,
  Edit2,
  Send,
} from "lucide-react";
import { colors, fonts, ENHANCEMENT_STAGES } from "../styles/theme";
import { useAuth } from "../hooks/useAuth";
import { Spinner, PageHeader, ErrorBanner, Button } from "../components/ui";
import {
  fetchEnhancements,
  createEnhancement,
  updateEnhancement,
  deleteEnhancement as deleteEnhancementApi,
  toggleEnhancementUpvote,
  addEnhancementComment,
  reorderEnhancements,
} from "../services/api";

export default function Enhancements() {
  const { user, role } = useAuth();
  const isAdmin = role === "admin";
  const userEmail = user?.userDetails || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Drag state
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const [moving, setMoving] = useState(null);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  // Inline editing (admin)
  const [editingField, setEditingField] = useState(null); // { field: "title"|"description" }
  const [editValue, setEditValue] = useState("");

  const cardRefs = useRef({});

  useEffect(() => {
    fetchEnhancements()
      .then((data) => setItems(data.enhancements || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Keep selectedItem in sync with items
  useEffect(() => {
    if (selectedItem) {
      const updated = items.find((i) => i.id === selectedItem.id);
      if (updated) setSelectedItem(updated);
    }
  }, [items]);

  // ── Drag Handlers (admin only) ─────────────────────────────

  const handleDragStart = useCallback((e, item) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
    if (e.currentTarget) e.currentTarget.style.opacity = "0.5";
  }, []);

  const handleDragEnd = useCallback((e) => {
    if (e.currentTarget) e.currentTarget.style.opacity = "1";
    setDragItem(null);
    setDropTarget(null);
    setDropIndex(-1);
  }, []);

  const handleDragOver = useCallback(
    (e, stageId) => {
      if (!dragItem) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDropTarget(stageId);

      // Compute drop index for within-column reordering
      const stageItems = items
        .filter((i) => i.status === stageId && i.id !== dragItem.id)
        .sort((a, b) => a.sortOrder - b.sortOrder);

      let idx = stageItems.length;
      for (let i = 0; i < stageItems.length; i++) {
        const el = cardRefs.current[stageItems[i].id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (e.clientY < rect.top + rect.height / 2) {
            idx = i;
            break;
          }
        }
      }
      setDropIndex(idx);
    },
    [dragItem, items],
  );

  const handleDragLeave = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropTarget(null);
      setDropIndex(-1);
    }
  }, []);

  const handleDrop = useCallback(
    async (e, toStage) => {
      e.preventDefault();
      setDropTarget(null);

      if (!dragItem) return;
      const item = dragItem;
      const targetIdx = dropIndex;
      setDragItem(null);
      setDropIndex(-1);
      setMoving(item.id);

      // Build the new column order
      const stageItems = items
        .filter((i) => i.status === toStage && i.id !== item.id)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const insertAt = Math.min(targetIdx, stageItems.length);
      stageItems.splice(insertAt, 0, { ...item, status: toStage });

      // Assign new sortOrder values
      const updates = stageItems.map((si, idx) => ({
        id: si.id,
        status: toStage,
        sortOrder: (idx + 1) * 1000,
      }));

      // Optimistic update
      setItems((prev) => {
        const next = prev.map((i) => {
          const upd = updates.find((u) => u.id === i.id);
          return upd ? { ...i, status: upd.status, sortOrder: upd.sortOrder } : i;
        });
        return next;
      });

      try {
        await reorderEnhancements(updates);
      } catch (err) {
        // Revert on failure — reload
        try {
          const data = await fetchEnhancements();
          setItems(data.enhancements || []);
        } catch {}
      } finally {
        setMoving(null);
      }
    },
    [dragItem, dropIndex, items],
  );

  // ── Actions ────────────────────────────────────────────────

  const handleAdd = async () => {
    if (!addTitle.trim()) return;
    setSubmitting(true);
    try {
      const result = await createEnhancement({
        title: addTitle.trim(),
        description: addDesc.trim(),
      });
      setItems((prev) => [...prev, result]);
      setAddTitle("");
      setAddDesc("");
      setShowAddForm(false);
    } catch (err) {
      alert("Failed to create: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (id) => {
    // Optimistic toggle
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        const upvotes = i.upvotes || [];
        const has = upvotes.some((u) => u.email === userEmail);
        const next = has
          ? upvotes.filter((u) => u.email !== userEmail)
          : [...upvotes, { email: userEmail, name: userEmail, at: new Date().toISOString() }];
        return { ...i, upvotes: next, upvoteCount: next.length };
      }),
    );
    try {
      const updated = await toggleEnhancementUpvote(id);
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
    } catch {}
  };

  const handleComment = async () => {
    if (!commentText.trim() || !selectedItem) return;
    setSendingComment(true);
    try {
      const updated = await addEnhancementComment(selectedItem.id, commentText.trim());
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setCommentText("");
    } catch (err) {
      alert("Failed to add comment: " + err.message);
    } finally {
      setSendingComment(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this enhancement?")) return;
    try {
      await deleteEnhancementApi(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setSelectedItem(null);
    } catch (err) {
      alert("Failed to delete: " + err.message);
    }
  };

  const handleEditSave = async () => {
    if (!editingField || !selectedItem) return;
    const updates = {};
    updates[editingField.field] = editValue;
    try {
      const updated = await updateEnhancement(selectedItem.id, updates);
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      alert("Failed to update: " + err.message);
    }
    setEditingField(null);
    setEditValue("");
  };

  // ── Render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spinner size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <ErrorBanner message={error} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Enhancements"
        subtitle={`${items.filter((i) => i.status !== "done").length} active · ${items.filter((i) => i.status === "done").length} completed`}
      >
        <Button variant="primary" icon={Plus} onClick={() => setShowAddForm(!showAddForm)}>
          Suggest Enhancement
        </Button>
      </PageHeader>

      {/* Add Form */}
      {showAddForm && (
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 20,
            marginBottom: 20,
          }}
        >
          <input
            autoFocus
            placeholder="Enhancement title..."
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: 14,
              fontFamily: fonts.body,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              outline: "none",
              marginBottom: 8,
              boxSizing: "border-box",
            }}
          />
          <textarea
            placeholder="Description (optional)..."
            value={addDesc}
            onChange={(e) => setAddDesc(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: 13,
              fontFamily: fonts.body,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              outline: "none",
              resize: "vertical",
              marginBottom: 12,
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleAdd}
              disabled={submitting || !addTitle.trim()}
              style={{
                padding: "7px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: fonts.body,
                background: colors.accent,
                color: colors.white,
                border: "none",
                borderRadius: 6,
                cursor: submitting ? "wait" : "pointer",
                opacity: submitting || !addTitle.trim() ? 0.5 : 1,
              }}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button
              onClick={() => { setShowAddForm(false); setAddTitle(""); setAddDesc(""); }}
              style={{
                padding: "7px 16px",
                fontSize: 13,
                fontFamily: fonts.body,
                background: "none",
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 16 }}>
        {ENHANCEMENT_STAGES.map((stage) => {
          const stageItems = items
            .filter((i) => i.status === stage.id)
            .sort((a, b) => a.sortOrder - b.sortOrder);
          const isOver = dropTarget === stage.id && dragItem && dragItem.status !== stage.id;
          const isSameCol = dropTarget === stage.id && dragItem && dragItem.status === stage.id;

          return (
            <div
              key={stage.id}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
              style={{
                minWidth: 260,
                flex: 1,
                background: isOver || isSameCol ? colors.accentDim : colors.bgCard,
                borderRadius: 8,
                border: `2px solid ${isOver || isSameCol ? colors.accent : colors.border}`,
                display: "flex",
                flexDirection: "column",
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              {/* Column Header */}
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color }} />
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: fonts.body, color: colors.text }}>
                    {stage.label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textSecondary,
                      background: "rgba(0,0,0,0.04)",
                      padding: "1px 6px",
                      borderRadius: 4,
                    }}
                  >
                    {stageItems.length}
                  </span>
                </div>
              </div>

              {/* Cards */}
              <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8, minHeight: 60, maxHeight: 560, overflowY: "auto" }}>
                {stageItems.length === 0 && (
                  <div style={{ fontSize: 12, color: colors.textSecondary, textAlign: "center", padding: "16px 8px" }}>
                    {isOver ? "Drop here" : "No items"}
                  </div>
                )}
                {stageItems.map((item, idx) => {
                  const hasUpvoted = (item.upvotes || []).some((u) => u.email === userEmail);
                  const isDone = stage.id === "done";
                  const isNew = Date.now() - new Date(item.createdAt).getTime() < 7 * 24 * 60 * 60 * 1000;
                  const showInsertBefore = (isOver || isSameCol) && dropIndex === idx;

                  return (
                    <div key={item.id}>
                      {/* Drop insertion indicator */}
                      {showInsertBefore && (
                        <div style={{ height: 3, background: colors.accent, borderRadius: 2, marginBottom: 6 }} />
                      )}
                      <EnhancementCard
                        item={item}
                        isDone={isDone}
                        isNew={isNew}
                        isAdmin={isAdmin}
                        hasUpvoted={hasUpvoted}
                        moving={moving === item.id}
                        onUpvote={() => handleUpvote(item.id)}
                        onClick={() => { setSelectedItem(item); setEditingField(null); setCommentText(""); }}
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragEnd={handleDragEnd}
                        cardRef={(el) => { cardRefs.current[item.id] = el; }}
                      />
                    </div>
                  );
                })}
                {/* Drop indicator at end */}
                {(isOver || isSameCol) && dropIndex === stageItems.length && (
                  <div style={{ height: 3, background: colors.accent, borderRadius: 2 }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <DetailModal
          item={selectedItem}
          isAdmin={isAdmin}
          userEmail={userEmail}
          commentText={commentText}
          setCommentText={setCommentText}
          sendingComment={sendingComment}
          onComment={handleComment}
          onUpvote={() => handleUpvote(selectedItem.id)}
          onDelete={() => handleDelete(selectedItem.id)}
          onClose={() => { setSelectedItem(null); setEditingField(null); }}
          editingField={editingField}
          editValue={editValue}
          onEditStart={(field) => { setEditingField({ field }); setEditValue(selectedItem[field] || ""); }}
          onEditChange={setEditValue}
          onEditSave={handleEditSave}
          onEditCancel={() => setEditingField(null)}
        />
      )}
    </div>
  );
}

// ── Card Component ─────────────────────────────────────────────

function EnhancementCard({ item, isDone, isNew, isAdmin, hasUpvoted, moving, onUpvote, onClick, onDragStart, onDragEnd, cardRef }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={cardRef}
      draggable={isAdmin}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        padding: 14,
        background: colors.bg,
        borderRadius: 6,
        border: `1px solid ${hovered ? colors.accent : colors.border}`,
        cursor: isAdmin ? "grab" : "pointer",
        position: "relative",
        transition: "border-color 0.15s",
        opacity: isDone ? 0.65 : moving ? 0.5 : 1,
      }}
    >
      {/* Drag handle (admin) */}
      {isAdmin && (
        <div style={{ position: "absolute", top: 10, right: 8, color: colors.textSecondary }}>
          <GripVertical size={14} />
        </div>
      )}

      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        {isDone && <Check size={13} color={colors.success} />}
        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: fonts.body, color: colors.text, lineHeight: 1.3 }}>
          {item.title}
        </span>
      </div>

      {/* Description preview */}
      {item.description && (
        <div
          style={{
            fontSize: 12,
            color: colors.textSecondary,
            lineHeight: 1.4,
            marginBottom: 8,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {item.description}
        </div>
      )}

      {/* Meta row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {/* Upvote */}
        <button
          onClick={(e) => { e.stopPropagation(); onUpvote(); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            fontFamily: fonts.body,
            color: hasUpvoted ? colors.accent : colors.textSecondary,
            fontWeight: hasUpvoted ? 700 : 400,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <ThumbsUp size={13} fill={hasUpvoted ? colors.accent : "none"} />
          {item.upvoteCount || 0}
        </button>

        {/* Comments */}
        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: colors.textSecondary }}>
          <MessageSquare size={13} />
          {item.commentCount || 0}
        </span>

        {/* New badge */}
        {isNew && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: colors.info,
              background: "rgba(99,102,241,0.10)",
              padding: "1px 6px",
              borderRadius: 4,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            New
          </span>
        )}

        {/* Creator */}
        <span style={{ fontSize: 11, color: colors.textSecondary, marginLeft: "auto" }}>
          {item.createdByName?.split("@")[0] || ""}
        </span>
      </div>
    </div>
  );
}

// ── Detail Modal ───────────────────────────────────────────────

function DetailModal({
  item, isAdmin, userEmail, commentText, setCommentText, sendingComment,
  onComment, onUpvote, onDelete, onClose,
  editingField, editValue, onEditStart, onEditChange, onEditSave, onEditCancel,
}) {
  const hasUpvoted = (item.upvotes || []).some((u) => u.email === userEmail);
  const stage = ENHANCEMENT_STAGES.find((s) => s.id === item.status);
  const comments = [...(item.comments || [])].reverse();

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bgCard,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          width: "100%",
          maxWidth: 580,
          maxHeight: "80vh",
          overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        {/* Modal Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            {/* Title */}
            {editingField?.field === "title" ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  autoFocus
                  value={editValue}
                  onChange={(e) => onEditChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onEditSave(); if (e.key === "Escape") onEditCancel(); }}
                  style={{
                    flex: 1,
                    fontSize: 17,
                    fontWeight: 700,
                    fontFamily: fonts.heading,
                    border: `1px solid ${colors.accent}`,
                    borderRadius: 4,
                    padding: "4px 8px",
                    outline: "none",
                  }}
                />
                <button onClick={onEditSave} style={{ background: "none", border: "none", cursor: "pointer", color: colors.success }}><Check size={16} /></button>
                <button onClick={onEditCancel} style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary }}><X size={16} /></button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h2 style={{ fontFamily: fonts.heading, fontSize: 17, fontWeight: 700, color: colors.text, margin: 0 }}>
                  {item.title}
                </h2>
                {isAdmin && (
                  <button onClick={() => onEditStart("title")} style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary, padding: 2 }}>
                    <Edit2 size={14} />
                  </button>
                )}
              </div>
            )}

            {/* Status badge + meta */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: stage?.color || colors.textSecondary, background: `${stage?.color || colors.textSecondary}18`, padding: "2px 8px", borderRadius: 4 }}>
                {stage?.label || item.status}
              </span>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                by {item.createdByName?.split("@")[0] || item.createdBy}
              </span>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                {formatDate(item.createdAt)}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Description */}
        <div style={{ padding: "16px 24px" }}>
          {editingField?.field === "description" ? (
            <div>
              <textarea
                autoFocus
                value={editValue}
                onChange={(e) => onEditChange(e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  fontSize: 13,
                  fontFamily: fonts.body,
                  border: `1px solid ${colors.accent}`,
                  borderRadius: 6,
                  padding: "8px 12px",
                  outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button onClick={onEditSave} style={{ padding: "4px 12px", fontSize: 12, fontFamily: fonts.body, background: colors.accent, color: colors.white, border: "none", borderRadius: 4, cursor: "pointer" }}>
                  Save
                </button>
                <button onClick={onEditCancel} style={{ padding: "4px 12px", fontSize: 12, fontFamily: fonts.body, background: "none", color: colors.textSecondary, border: `1px solid ${colors.border}`, borderRadius: 4, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <p style={{ fontSize: 13, fontFamily: fonts.body, color: item.description ? colors.text : colors.textSecondary, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap", flex: 1 }}>
                {item.description || "No description."}
              </p>
              {isAdmin && (
                <button onClick={() => onEditStart("description")} style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary, padding: 2, flexShrink: 0 }}>
                  <Edit2 size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Upvotes + Actions */}
        <div style={{ padding: "0 24px 16px", display: "flex", alignItems: "center", gap: 12, borderBottom: `1px solid ${colors.border}` }}>
          <button
            onClick={onUpvote}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              fontSize: 13,
              fontFamily: fonts.body,
              fontWeight: hasUpvoted ? 700 : 400,
              color: hasUpvoted ? colors.accent : colors.textSecondary,
              background: hasUpvoted ? colors.accentDim : "none",
              border: `1px solid ${hasUpvoted ? colors.accent : colors.border}`,
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            <ThumbsUp size={14} fill={hasUpvoted ? colors.accent : "none"} />
            {item.upvoteCount || 0} {(item.upvoteCount || 0) === 1 ? "vote" : "votes"}
          </button>

          {(item.upvotes || []).length > 0 && (
            <span style={{ fontSize: 12, color: colors.textSecondary }}>
              {(item.upvotes || []).map((u) => u.name?.split("@")[0] || u.email?.split("@")[0]).join(", ")}
            </span>
          )}

          {isAdmin && (
            <button
              onClick={onDelete}
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "6px 10px",
                fontSize: 12,
                fontFamily: fonts.body,
                color: colors.danger,
                background: "none",
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <Trash2 size={13} />
              Delete
            </button>
          )}
        </div>

        {/* Comments */}
        <div style={{ padding: "16px 24px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: fonts.body, color: colors.text, marginBottom: 12 }}>
            Comments ({item.commentCount || 0})
          </div>

          {/* Comment input */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <input
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleComment()}
              style={{
                flex: 1,
                padding: "8px 12px",
                fontSize: 13,
                fontFamily: fonts.body,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                outline: "none",
              }}
            />
            <button
              onClick={onComment}
              disabled={sendingComment || !commentText.trim()}
              style={{
                padding: "8px 12px",
                background: colors.accent,
                color: colors.white,
                border: "none",
                borderRadius: 6,
                cursor: sendingComment ? "wait" : "pointer",
                opacity: sendingComment || !commentText.trim() ? 0.5 : 1,
              }}
            >
              <Send size={14} />
            </button>
          </div>

          {/* Comment list */}
          {comments.length === 0 && (
            <div style={{ fontSize: 12, color: colors.textSecondary, textAlign: "center", padding: 12 }}>
              No comments yet.
            </div>
          )}
          {comments.map((c) => (
            <div key={c.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${colors.borderLight}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>
                  {c.name?.split("@")[0] || c.email?.split("@")[0]}
                </span>
                <span style={{ fontSize: 11, color: colors.textSecondary }}>{formatDate(c.at)}</span>
              </div>
              <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                {c.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  function handleComment() {
    if (!commentText.trim()) return;
    onComment();
  }
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
